import Box from "@material-ui/core/Box";
import Checkbox from "@material-ui/core/Checkbox";
import CircularProgress from "@material-ui/core/CircularProgress";
import FormControlLabel from "@material-ui/core/FormControlLabel";
import React, { Fragment, useEffect, useState } from "react";
import { useHttp } from "../../services/HttpService";
import { logger } from "../../services/LogService";
import { deleteNachricht, getAlleNachrichtenEndpoint, setNachrichtGelesen } from "../../services/NachrichtenService";
import { createCalculatedColumn, createColumn, createComponentColumn, createDateColumn } from "../../utils/ColumnUtils";
import EAButton from "../EAButton";
import EADialog from "../EADialog";
import EATable from "../EATable";
import EATableActionButton from "../tableComponents/EATableActionButton";
import { DESC } from "../../utils/ArrayUtils";
import { createDirectLink } from "../../utils/RouteUtils";
import {useConfirmation} from "../ConfirmationDialog/ConfirmationDialogProvider";
import {confirmDeleteOption} from "../ConfirmationDialog/confirmationDialogOptions";

const id = "NachrichtenArbeitsliste";

function NachrichtenArbeitsliste({ history, refetchNachrichten }) {
    const confirm = useConfirmation();
    const [showDialog, setShowDialog] = useState(false);
    const [deleteNachrichtOnClose, setDeleteNachrichtOnClose] = useState(false);
    const [{ data: nachrichten, loading: nachrichtenLoading }] = useHttp(getAlleNachrichtenEndpoint);
    const [volatileNachrichten, setVolatileNachrichten] = useState([]);
    const [selectedNachricht, setSelectedNachricht] = useState([]);
    const [linkLoading, setLinkLoading] = useState(false);
    const nachrichtenColumns = [
        createComponentColumn("id", (row, index) => {
            return (
            <div className="gridl" style={{gridTemplateColumns: "repeat(2,min-content)", marginLeft: "8px"}}>
                <EATableActionButton id={"ab-" + index} iconName="ico-view">
                                        <span
                                            onClick={() => showNachrichtDetails(row)}
                                            className={"w-100"}
                                            style={{
                                                height: "100%",
                                                display: "block",
                                            }}
                                        />
                </EATableActionButton>
                <EATableActionButton id={"deleteWert-" + index} iconName="ico-delete">
                                        <span
                                            onClick={() => handleDelete(row)}
                                            className={"w-100"}
                                            style={{
                                                height: "100%",
                                                display: "block",
                                            }}
                                        />
                </EATableActionButton>
            </div>
            );
        }),
        createCalculatedColumn("gelesen", (row) => {
            return row.gelesen ? "gelesen" : "neu";
        }),
        createDateColumn("absendeDatum", "Datum"),
        createColumn("bezugZuFachobjekt", "Bezug"),
        createColumn("absender", "Absender"),
        createColumn("betreff", "Betreff"),
    ];

    function createDialogTitle() {
        return (
            <Box color="#757575">
                Nachricht vom {new Date(selectedNachricht.absendeDatum).toLocaleDateString()} von {selectedNachricht.absender}
            </Box>
        );
    }

    function createDialogContent() {
        if (linkLoading) {
            return (
                <div
                    style={{
                        width: "500px",
                        height: "150px",
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                    }}
                >
                    <CircularProgress />
                </div>
            );
        }
        return (
            <Fragment>
                <div style={{ margin: 30 }}>
                    <p style={{ fontSize: 13, fontWeight: "bold", marginBottom: 10 }}>{selectedNachricht.betreff}</p>
                    <p style={{ margin: "16px 0" }}>{selectedNachricht.text}</p>
                    <div>
                        {createDirectLink(
                            selectedNachricht.fachobjektbezug,
                            selectedNachricht.bezugZuFachobjekt,
                            selectedNachricht.fachobjektId,
                            () => {
                                deleteNachrichtIfIntended(false);
                                setLinkLoading(true);
                            },
                            history
                        )}
                    </div>
                </div>
            </Fragment>
        );
    }

    function handleDialogClose(updateNachrichtenListe) {
        setShowDialog(false);
        deleteNachrichtIfIntended(updateNachrichtenListe);
        setDeleteNachrichtOnClose(false);
    }

    function handleDelete(nachricht) {
        confirm(
            confirmDeleteOption(
                "die Nachricht",
                () => deleteMessage(nachricht.id, true),
                () => {}
            )
        );
    }

    function deleteNachrichtIfIntended(updateNachrichtenListe) {
        if (deleteNachrichtOnClose) {
            deleteMessage(selectedNachricht.id, updateNachrichtenListe);
        }
    }

    function deleteMessage(nachrichtId, updateNachrichtenListe) {
        deleteNachricht(nachrichtId)
            .then(() => {
                if (updateNachrichtenListe) {
                    const updatedNachrichten = volatileNachrichten.filter((n) => n.id !== nachrichtId);
                    setVolatileNachrichten(updatedNachrichten);
                    refetchNachrichten();
                }
            })
            .catch((e) => {
                logger.error(e);
            });
    }

    function showNachrichtDetails(nachricht) {
        setSelectedNachricht(nachricht);
        setShowDialog(true);
        markAsReadIfUnread(nachricht);
    }

    function markAsReadIfUnread(nachricht) {
        if (nachricht.gelesen) {
            return;
        }
        setNachrichtGelesen(nachricht.id)
            .then((result) => {
                refetchNachrichten();
                updateNachrichten(result);
            })
            .catch((e) => {
                logger.error(e);
            });
    }

    function updateNachrichten(result) {
        const index = nachrichten.findIndex((n) => {
            return n.id === result.data.id;
        });
        const updatedNachrichten = [...volatileNachrichten];
        updatedNachrichten[index] = result.data;
        setVolatileNachrichten(updatedNachrichten);
    }

    const handleDeleteCheckboxChange = (e) => {
        setDeleteNachrichtOnClose(e.target.checked);
    };

    useEffect(() => {
        setVolatileNachrichten(nachrichten);
    }, [nachrichten]);

    return (
        <Fragment>
            <EATable
                id={id}
                order={DESC}
                loading={nachrichtenLoading}
                columns={nachrichtenColumns}
                data={volatileNachrichten}
                style={{ marginTop: "var(--vertical-spacing)" }}
            />
            <EADialog
                open={showDialog}
                titleChildren={createDialogTitle()}
                content={createDialogContent()}
                buttons={
                    !linkLoading && (
                        <Fragment>
                            <Box mr={2}>
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            checked={deleteNachrichtOnClose}
                                            onChange={handleDeleteCheckboxChange}
                                            name="checkedB"
                                            color="primary"
                                        />
                                    }
                                    label="Diese Nachricht beim Schließen löschen"
                                    labelPlacement={"start"}
                                />
                            </Box>
                            <EAButton
                                id={"eins"}
                                data-cy={"nachrichtSchliessenButton"}
                                label={"Schließen"}
                                onClick={() => handleDialogClose(deleteNachrichtOnClose)}
                            />
                        </Fragment>
                    )
                }
                closeFunction={() => handleDialogClose(deleteNachrichtOnClose)}
            />
        </Fragment>
    );
}

export default NachrichtenArbeitsliste;
